import React, { useEffect, useState } from "react";
import { Card, Divider, Typography } from "antd";
import { purchaseOrderRecordnWithDetailsApi } from "../../../../../apiservice/purchase";
import { numberFormat, convertNumberToWords, formatToCompanyTimezone } from "../../../../../lib/functions";
import "./purchaseOrderVoucher.css";
import "./purchaseOrderVoucherMobile.css";
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../../../Auth/authSelectors";

const { Title, Text } = Typography;

const PurchaseOrderVoucherBodyMobile = ({ pur_o_id, institution, showStamp = false,
  showSignature = false, setVoucherNo, setAccName, setSupplierEmail }) => {
  const [data, setData] = useState(null);
  const authInfo = useSelector(selectAuthInfo);
  const isBonusItem = (item) => Number(item.item_rate) === 0;
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";

  useEffect(() => {
    if (!pur_o_id) return;
    purchaseOrderRecordnWithDetailsApi({ pur_o_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) {
        const record = res.data[0];
        setData(record);
        if (setVoucherNo) {
          setVoucherNo(record.pur_order_no);
          setAccName(record.acc_name);
        }
        if (record.email_address) {
          setSupplierEmail?.(record.email_address);
        }
      }
    });
  }, [pur_o_id]);

  if (!data) return null;

  if (data.approval_status === "pending") {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Title level={5}>Pending Approval</Title>
        <Text>This purchase order is pending approval and cannot be viewed yet.</Text>
      </div>
    );
  }

  const isIndividual = data.is_cal_type === "individual";

  const uniqueItemsMap = new Map();
  (data.details || []).forEach((item) => {
    const key = `${item.pur_d_id}-${item.item_id}`;
    if (!uniqueItemsMap.has(key)) {
      uniqueItemsMap.set(key, item);
    }
  });

  const items = Array.from(uniqueItemsMap.values()).map((item) => ({
    ...item,
    qty_display:
      item?.item_qty + " " + item?.unit_symbol +
      (item?.conversion > 1
        ? ", " + item?.retail_qty + " " + item?.base_unit_name
        : ""),
    done_qty_display:
      item?.done_item_qty + " " + item?.unit_symbol +
      (item?.conversion > 1
        ? ", " + item?.done_retail_qty + " " + item?.base_unit_name
        : ""),
  }));

  const serviceCost = (data.otherServiceTrans || []).reduce(
    (sum, row) => sum + Number(row.tran_amount || 0),
    0
  );

  const hasBonusItems = items.some(isBonusItem);

  const totals = items.reduce(
    (acc, d) => {
      const qty = d.item_qty || d.pur_o_qty || 0;
      const rate = Number(d.item_rate || 0);
      const base = qty * rate;
      const discountAmt = (Number(d.item_discount_per || 0) / 100) * base;
      const afterDiscount = base - discountAmt;
      const taxAmt = (Number(d.item_tax_per || 0) / 100) * afterDiscount;

      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;
      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
  );

  const grandTotal = isIndividual
    ? totals.grandTotal + serviceCost
    : (data?.total_amount || 0) + serviceCost;

  const totalTax = isIndividual
    ? totals.taxTotal
    : Number(data?.total_tax || 0);

  const itemAmount = (item) => {
    if (isIndividual) {
      let qty = item.item_qty || 0;
      let rate = Number(item.item_rate || 0);
      let total = qty * rate;
      total -= (Number(item.item_discount_per) / 100) * total;
      total += (Number(item.item_tax_per) / 100) * total;
      return total;
    }
    return Number(item.item_total || 0);
  };

  return (
    <div className="po-mobile-voucher">
      {/* Bill to / meta */}
      <Card size="small" className="po-mobile-section">
        <Text strong>Supplier/ Creditor Code:</Text> <Text>{data.acc_code}</Text>
        <br />
        <Text strong>Name:</Text> <Text>{data.acc_name}</Text>
        <br />
        <Text strong>Address:</Text> <Text>{data.address}</Text>
        <br />
        <Text strong>Contact No:</Text> <Text>{data.contact_no}</Text>
      </Card>

      <Card size="small" className="po-mobile-section">
        <Title level={5} style={{ marginTop: 0 }}>PURCHASE ORDER</Title>
        <Text strong>Voucher No:</Text> <Text>{data.pur_order_no}</Text>
        <br />
        <Text strong>Order Date:</Text>{" "}
        <Text>{formatToCompanyTimezone(data.created_date)?.split(" ")[0]}</Text>
        <br />
        <Text strong>Order By:</Text> <Text>{data.full_name?.split(" ")[0]}</Text>
        <br />
        <Text strong>Address:</Text> <Text>{institution?.street_address}</Text>
        <br />
        {institution?.phone_no && (
          <>
            <Text strong>Contact No:</Text> <Text>{institution.phone_no}</Text>
            <br />
          </>
        )}
        <Text strong>Payment Terms:</Text> <Text>{data.payment_terms}</Text>
        <br />
        {institution?.vat_license && (
          <>
            <Text strong>
              {institution?.country?.trim().toLowerCase() === "united arab emirates"
                ? "TRN No:"
                : "Tax Reg No:"}
            </Text>{" "}
            <Text>{institution.vat_license}</Text>
            <br />
          </>
        )}
        {data.licence_no && (
          <>
            <Text strong>License No:</Text> <Text>{data.licence_no}</Text>
          </>
        )}
      </Card>

      {/* Items as cards */}
      <div className="po-mobile-items">
        {items.map((item, idx) => (
          <Card
            key={`${item.pur_d_id}-${item.item_id}`}
            size="small"
            className={`po-mobile-item-card ${isBonusItem(item) ? "bonus-row" : ""}`}
          >
            <div className="po-mobile-item-header">
              <Text strong>{idx + 1}. {item.item_name}</Text>
              {isBonusItem(item) && <span className="bonus-badge">BONUS</span>}
            </div>
            {item.item_description && (
              <div style={{ fontSize: 11, color: "#666", marginBottom: 4, whiteSpace: "pre-wrap" }}>
                {item.item_description}
              </div>
            )}

            <div className="po-mobile-item-row">
              <Text type="secondary">Rate</Text>
              <Text>{Number(item.item_rate).toFixed(2)}</Text>
            </div>

            <div className="po-mobile-item-row">
              <Text type="secondary">Per</Text>
              <Text>{isIndividual ? item.unit_name : item.per_unit_symbol}</Text>
            </div>

            <div className="po-mobile-item-row">
              <Text type="secondary">{isIndividual ? "Qty" : "Order Quantity"}</Text>
              <Text>{item.qty_display}</Text>
            </div>

            {!isIndividual && (
              <div className="po-mobile-item-row">
                <Text type="secondary">Sold Quantity</Text>
                <Text>{item.done_qty_display}</Text>
              </div>
            )}

            {isIndividual && (
              <>
                <div className="po-mobile-item-row">
                  <Text type="secondary">Disct%</Text>
                  <Text>{item.item_discount_per}</Text>
                </div>
                <div className="po-mobile-item-row">
                  <Text type="secondary">{taxLabel}</Text>
                  <Text>{item.item_tax_per}</Text>
                </div>
              </>
            )}

            <div className="po-mobile-item-row po-mobile-item-total">
              <Text strong>{isIndividual ? "Total" : "Amount"}</Text>
              <Text strong>{itemAmount(item).toFixed(2)}</Text>
            </div>
          </Card>
        ))}
      </div>

      {hasBonusItems && (
        <Text style={{ display: "block", fontSize: 11, color: "#666", marginTop: 10 }}>
          *Bonus items are supplied free of cost and not included in taxable value.
        </Text>
      )}

      {/* Totals */}
      <Divider />
      <div className="po-mobile-totals">
        <div className="po-mobile-item-row">
          <Text>Sub Total:</Text>
          <Text>{numberFormat(isIndividual ? totals.subTotal : data.sub_total || 0)}</Text>
        </div>
        <div className="po-mobile-item-row">
          <Text>Discount:</Text>
          <Text>{numberFormat(isIndividual ? totals.discountTotal : data.total_discount)}</Text>
        </div>
        <div className="po-mobile-item-row">
          <Text>{taxLabel}:</Text>
          <Text>{numberFormat(totalTax)}</Text>
        </div>
        {serviceCost > 0 && (
          <div className="po-mobile-item-row">
            <Text>Other Expenses:</Text>
            <Text>{numberFormat(serviceCost)}</Text>
          </div>
        )}
        <div className="po-mobile-item-row po-mobile-item-total">
          <Text strong>Grand Total ({institution?.currency}):</Text>
          <Text strong>{numberFormat(data.total_amount)}</Text>
        </div>
        <div className="po-mobile-item-row po-mobile-item-total">
          <Text strong>Paid ({institution?.currency}):</Text>
          <Text strong>{numberFormat(data.paid_total || 0)}</Text>
        </div>
        <div className="po-mobile-item-row">
          <Text strong>Due ({institution?.currency}):</Text>
          <Text strong>{numberFormat(data.due_total || 0)}</Text>
        </div>
      </div>

      <Divider />
      <Text className="wrap-text">
        In Word of Grand Total : {convertNumberToWords(grandTotal, data?.currency || institution?.currency)}
      </Text>
      <br />
      <Text className="wrap-text">Description : {data.narration || "-"}</Text>

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">
          <div className="footer-right-group">
            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}
            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}
          </div>
        </div>
      )}

      {data?.show_terms_conditions === "yes" && data?.payment_terms_description && (
        <div className="po-payment-terms-page">
          <div className="po-payment-terms-content">
            <Title level={4} style={{ textAlign: "center" }}>
              Payment Terms
            </Title>
            <ul>
              {data.payment_terms_description
                .split("\n")
                .filter((line) => line.trim())
                .map((line, idx) => (
                  <li key={idx}>{line.replace(/^•\s*/, "")}</li>
                ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderVoucherBodyMobile;
